'use client';

import React, { useState, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Table,
  Typography,
  InputNumber,
  Divider,
  Tag,
  Space,
} from 'antd';
import { toast } from 'react-toastify';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SendOutlined,
  HistoryOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { MASTER_ITEMS } from '@/lib/masterItems';
import type { PRRecord, PendingPRItemCandidate } from '@/lib/mockPrStore';

const { Text } = Typography;

interface PRModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editRecord?: PRRecord | null;
  initialPendingItems?: Array<{
    itemId: string;
    itemCode: string;
    itemName: string;
    category: string;
    uomCode: string;
    requestedQty: number;
    notes?: string;
  }>;
  initialTitle?: string;
  initialDepartment?: string;
}

interface FormLineItem {
  key: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  category: string;
  uomCode: string;
  requestedQty: number;
  notes?: string;
  reappliedFromPrItemId?: string;
}

// Standard Factory & Enterprise Departments LOV
const DEPARTMENT_OPTIONS = [
  { value: 'Procurement & Logistics', label: 'Procurement & Logistics' },
  { value: 'Garment Manufacturing Division', label: 'Garment Manufacturing Division' },
  { value: 'Denim Production Division', label: 'Denim Production Division' },
  { value: 'Warehouse & Packaging Department', label: 'Warehouse & Packaging Department' },
  { value: 'Maintenance & Workshop Division', label: 'Maintenance & Workshop Division' },
  { value: 'Quality Assurance & QC Lab', label: 'Quality Assurance & QC Lab' },
  { value: 'Cutting & Sample Room', label: 'Cutting & Sample Room' },
  { value: 'Finishing & Packing Line', label: 'Finishing & Packing Line' },
  { value: 'Facility & General Affairs', label: 'Facility & General Affairs' },
  { value: 'Finance & Accounting', label: 'Finance & Accounting' },
];

// Material Item Searchable LOV with top 10 limit for optimal performance (no nominals)
function MaterialItemSelect({
  value,
  onChange,
  excludeItemIds = [],
  catalogItems = MASTER_ITEMS,
}: {
  value: string;
  onChange: (itemId: string) => void;
  excludeItemIds?: string[];
  catalogItems?: typeof MASTER_ITEMS;
}) {
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    let matches = catalogItems;
    if (q) {
      matches = catalogItems.filter(
        (m) =>
          m.code.toLowerCase().includes(q) ||
          m.name.toLowerCase().includes(q) ||
          m.category.toLowerCase().includes(q) ||
          (m.specs && m.specs.toLowerCase().includes(q))
      );
    }
    // Limit to max 10 items for optimal performance
    const top10 = matches.slice(0, 10);

    // Keep current selected item visible if not in top 10
    const currentItem = catalogItems.find((m) => m.id === value);
    if (currentItem && !top10.some((m) => m.id === currentItem.id)) {
      return [currentItem, ...top10.slice(0, 9)];
    }

    return top10;
  }, [searchValue, value, catalogItems]);

  return (
    <Select
      showSearch
      value={value}
      placeholder="Ketik untuk cari item (maks 10 hasil)..."
      filterOption={false}
      onSearch={(text) => setSearchValue(text)}
      onChange={(val) => {
        onChange(val);
        setSearchValue('');
      }}
      style={{ width: '100%' }}
      dropdownStyle={{ maxHeight: 320 }}
      options={filteredOptions.map((m) => {
        const isAlreadySelected = excludeItemIds.includes(m.id) && m.id !== value;
        return {
          value: m.id,
          disabled: isAlreadySelected,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
              <div>
                <div style={{ fontWeight: 600, color: isAlreadySelected ? '#94A3B8' : '#1E293B', fontSize: 13 }}>
                  {m.name} {isAlreadySelected && <span style={{ color: '#EF4444', fontSize: 11 }}>(Sudah dipilih)</span>}
                </div>
                <div style={{ fontSize: 11, color: isAlreadySelected ? '#CBD5E1' : '#6366F1' }}>
                  {m.code} • {m.category}
                </div>
              </div>
              <Tag color={isAlreadySelected ? 'default' : 'blue'} style={{ fontSize: 10, borderRadius: 4, margin: 0 }}>
                {m.uomCode}
              </Tag>
            </div>
          ),
        };
      })}
    />
  );
}

export default function PRCreateEditModal({
  open,
  onClose,
  onSuccess,
  editRecord,
  initialPendingItems,
  initialTitle,
  initialDepartment,
}: PRModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState<FormLineItem[]>([]);
  const [catalogItems, setCatalogItems] = useState(MASTER_ITEMS);

  React.useEffect(() => {
    async function loadCatalog() {
      try {
        const res = await fetch('/api/items');
        const json = await res.json();
        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          setCatalogItems(json.data);
        }
      } catch (err) {
        console.error('Failed to load items catalog:', err);
      }
    }
    if (open) {
      loadCatalog();
    }
  }, [open]);

  // Pending items from other PRs selection state
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [pendingCandidates, setPendingCandidates] = useState<PendingPRItemCandidate[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [selectedPendingKeys, setSelectedPendingKeys] = useState<string[]>([]);
  const [pendingSearch, setPendingSearch] = useState('');

  // Pre-fill form on open / edit
  React.useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (editRecord) {
          form.setFieldsValue({
            title: editRecord.title || '',
            department: editRecord.department,
            notes: editRecord.notes,
          });
          setItems(
            editRecord.items.map((it) => ({
              key: it.id,
              itemId: it.itemId,
              itemCode: it.itemCode,
              itemName: it.itemName,
              category: it.category,
              uomCode: it.uomCode,
              requestedQty: it.requestedQty,
              notes: it.notes,
            }))
          );
        } else if (initialPendingItems && initialPendingItems.length > 0) {
          form.resetFields();
          form.setFieldsValue({
            title: initialTitle || 'Pengadaan Kebutuhan Material Tertunda',
            department: initialDepartment || 'Procurement & Logistics',
            notes: 'Re-pengajuan item material yang belum disetujui / pending dari PR sebelumnya',
          });
          setItems(
            initialPendingItems.map((it, idx) => ({
              key: `line-init-pending-${idx}`,
              itemId: it.itemId,
              itemCode: it.itemCode,
              itemName: it.itemName,
              category: it.category,
              uomCode: it.uomCode,
              requestedQty: it.requestedQty,
              notes: it.notes || '',
            }))
          );
        } else {
          form.resetFields();
          form.setFieldsValue({
            title: '',
            department: 'Procurement & Logistics',
            notes: '',
          });
          const first = MASTER_ITEMS[0];
          setItems([
            {
              key: `line-1`,
              itemId: first.id,
              itemCode: first.code,
              itemName: first.name,
              category: first.category,
              uomCode: first.uomCode,
              requestedQty: 100,
              notes: '',
            },
          ]);
        }
      }, 0);
    }
  }, [open, editRecord, initialPendingItems, initialTitle, initialDepartment, form]);

  const handleOpenPendingModal = async () => {
    setLoadingPending(true);
    setPendingModalOpen(true);
    try {
      const res = await fetch('/api/purchase-requests/pending-items');
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setPendingCandidates(json.data);
      }
    } catch {
      toast.error('Gagal mengambil daftar item pending');
    } finally {
      setLoadingPending(false);
    }
  };

  const handleInsertPendingItems = () => {
    const selected = pendingCandidates.filter((c) => selectedPendingKeys.includes(c.prItemId));
    if (selected.length === 0) return;

    let addedCount = 0;
    setItems((prev) => {
      const existingItemIds = new Set(prev.map((i) => i.itemId));
      const newItems: FormLineItem[] = [...prev];

      for (const c of selected) {
        if (!existingItemIds.has(c.itemId)) {
          existingItemIds.add(c.itemId);
          newItems.push({
            key: `line-pending-${c.prItemId}-${Date.now()}`,
            itemId: c.itemId,
            itemCode: c.itemCode,
            itemName: c.itemName,
            category: c.category,
            uomCode: c.uomCode,
            requestedQty: c.requestedQty,
            notes: c.notes ? `${c.notes} (Re-pengajuan dari ${c.prNumber})` : `Re-pengajuan dari ${c.prNumber}`,
          });
          addedCount++;
        }
      }
      return newItems;
    });

    if (addedCount > 0) {
      toast.success(`${addedCount} item pending berhasil dimasukkan ke pengajuan PR!`);
    } else {
      toast.info('Item yang dipilih sudah ada dalam daftar pengajuan.');
    }
    setSelectedPendingKeys([]);
    setPendingModalOpen(false);
  };

  const handleAddItem = () => {
    // Pick the first catalog item not yet in the items list to prevent duplicates
    const usedIds = new Set(items.map((it) => it.itemId));
    const nextAvailableItem = catalogItems.find((m) => !usedIds.has(m.id)) || MASTER_ITEMS.find((m) => !usedIds.has(m.id));

    if (!nextAvailableItem) {
      toast.info('Semua item dari katalog sudah ada dalam pengajuan ini.');
      return;
    }

    const newKey = `line-${Date.now()}`;
    setItems((prev) => [
      ...prev,
      {
        key: newKey,
        itemId: nextAvailableItem.id,
        itemCode: nextAvailableItem.code,
        itemName: nextAvailableItem.name,
        category: nextAvailableItem.category,
        uomCode: nextAvailableItem.uomCode,
        requestedQty: 10,
        notes: '',
      },
    ]);
  };

  const handleRemoveItem = (key: string) => {
    if (items.length <= 1) {
      toast.warning('Pengajuan PR harus memiliki minimal 1 item.');
      return;
    }
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const handleItemSelect = (key: string, selectedItemId: string) => {
    // Duplicate check: Prevent selecting an item already chosen in another line
    const isAlreadySelected = items.some((it) => it.key !== key && it.itemId === selectedItemId);
    if (isAlreadySelected) {
      const existing = catalogItems.find((m) => m.id === selectedItemId) || MASTER_ITEMS.find((m) => m.id === selectedItemId);
      toast.warning(`Item "${existing?.name || selectedItemId}" sudah dipilih pada baris lain. Gabungkan jumlah kuantitas pada baris yang sudah ada.`);
      return;
    }

    const catalogItem = catalogItems.find((m) => m.id === selectedItemId) || MASTER_ITEMS.find((m) => m.id === selectedItemId);
    if (!catalogItem) return;

    setItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it;
        return {
          ...it,
          itemId: catalogItem.id,
          itemCode: catalogItem.code,
          itemName: catalogItem.name,
          category: catalogItem.category,
          uomCode: catalogItem.uomCode,
        };
      })
    );
  };

  const handleQtyChange = (key: string, qty: number | null) => {
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, requestedQty: qty || 0 } : it))
    );
  };

  const handleItemNotesChange = (key: string, text: string) => {
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, notes: text } : it))
    );
  };

  // Summary counts (no nominals)
  const totalItemsCount = items.length;
  const totalRequestedUnits = items.reduce(
    (acc, it) => acc + (it.requestedQty || 0),
    0
  );

  const handleSubmit = async (submitImmediately: boolean) => {
    try {
      const values = await form.validateFields();

      if (items.length === 0) {
        toast.error('Tambahkan minimal 1 item material.');
        return;
      }

      // Check strictly positive quantity validation
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!it.requestedQty || it.requestedQty <= 0) {
          toast.error(
            `Item #${i + 1} (${it.itemName}): Jumlah kuantitas harus lebih besar dari 0.`
          );
          return;
        }
      }

      // Duplicate Item Invariant: No duplicate items in one PR
      const itemIds = items.map((it) => it.itemId);
      const duplicates = itemIds.filter((id, index) => itemIds.indexOf(id) !== index);
      if (duplicates.length > 0) {
        const dupItem = MASTER_ITEMS.find((m) => m.id === duplicates[0]);
        toast.error(
          `Item '${dupItem?.name || duplicates[0]}' tidak boleh duplikat dalam satu PR. Gabungkan jumlah kuantitas pada baris yang sudah ada.`
        );
        return;
      }

      setSubmitting(true);

      const payload = {
        title: values.title?.trim(),
        department: values.department,
        notes: values.notes?.trim() || '',
        items: items.map((it) => ({
          itemId: it.itemId,
          requestedQty: it.requestedQty,
          notes: it.notes,
          reappliedFromPrItemId: it.reappliedFromPrItemId,
        })),
        submitImmediately,
      };

      const url = editRecord
        ? `/api/purchase-requests/${editRecord.id}`
        : '/api/purchase-requests';
      const method = editRecord ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Gagal menyimpan pengajuan PR');
      }

      toast.success(data.message || 'Pengajuan PR berhasil disimpan');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Terjadi kendala saat menyimpan';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 6 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: '#EEF2FF',
              color: '#6366F1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {editRecord ? '✎' : '+'}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
              {editRecord ? `Edit Pengajuan PR ${editRecord.title || editRecord.prNumber}` : 'Buat Pengajuan Pembelian (PR Baru)'}
            </div>
            <div style={{ fontSize: 12, color: '#64748B', fontWeight: 400 }}>
              Tentukan judul, departemen pemohon, justifikasi, serta daftar material dan jumlah yang dibutuhkan.
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnHidden
      style={{ top: 24 }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        {/* Row 1: Judul / Tujuan Pengadaan (Full Width) */}
        <Form.Item
          name="title"
          label="Judul / Tujuan Pengadaan"
          rules={[{ required: true, message: 'Masukkan judul atau tujuan pengadaan' }]}
        >
          <Input
            placeholder="Contoh: Pengadaan Bahan Baku Denim & Zipper Proyek Jaket Ekspor"
            style={{ borderRadius: 8, height: 40 }}
          />
        </Form.Item>

        {/* Row 2: Departemen / Unit Pemohon (Full Width Searchable LOV) */}
        <Form.Item
          name="department"
          label="Departemen / Unit Pemohon"
          rules={[{ required: true, message: 'Pilih departemen pemohon' }]}
        >
          <Select
            showSearch
            placeholder="Pilih atau cari departemen pemohon..."
            options={DEPARTMENT_OPTIONS}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ borderRadius: 8, height: 40 }}
          />
        </Form.Item>

        {/* Row 3: Catatan / Justifikasi Pengadaan (Full Width TextArea) */}
        <Form.Item
          name="notes"
          label="Catatan / Justifikasi Pengadaan"
        >
          <Input.TextArea
            rows={3}
            placeholder="Tuliskan justifikasi kebutuhan pengadaan, rencana penggunaan material, urgensi jadwal, atau proyek terkait..."
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        {/* Requisition Line Items Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong style={{ fontSize: 14, color: '#1E293B' }}>
              Daftar Item Material ({items.length})
            </Text>
            <span
              style={{
                fontSize: 11,
                color: '#64748B',
                background: '#F1F5F9',
                padding: '2px 8px',
                borderRadius: 12,
              }}
            >
              Kuantitas &gt; 0
            </span>
          </div>

          <Space>
            <Button
              type="default"
              icon={<HistoryOutlined />}
              onClick={handleOpenPendingModal}
              style={{
                borderColor: '#F59E0B',
                color: '#D97706',
                background: '#FEF3C7',
                fontWeight: 600,
                borderRadius: 8,
              }}
            >
              + Ambil Item Pending dari PR Lain
            </Button>

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddItem}
              style={{
                borderColor: '#818CF8',
                color: '#4F46E5',
                background: '#EEF2FF',
                fontWeight: 600,
                borderRadius: 8,
              }}
            >
              + Tambah Baris Item Baru
            </Button>
          </Space>
        </div>

        {/* Structured Table: 1 Row per Item with 6 Columns (Notes spans colSpan 3) */}
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', background: '#FFFFFF' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1.5px solid #E2E8F0' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#475569', width: '32%' }}>
                  Item Material (Katalog LOV) <span style={{ color: '#EF4444' }}>*</span>
                </th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#475569', width: '15%' }}>
                  Jumlah Diminta <span style={{ color: '#EF4444' }}>*</span>
                </th>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#475569', width: '9%' }}>
                  Satuan
                </th>
                <th colSpan={3} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#475569', width: '39%' }}>
                  Catatan / Spesifikasi Teknis
                </th>
                <th style={{ padding: '10px 8px', textAlign: 'center', width: '5%' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((record, index) => (
                <tr key={record.key} style={{ borderBottom: '1px solid #E2E8F0', background: '#FFFFFF' }}>
                  {/* Col 1 (colspan 1): Item Material */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          background: '#EEF2FF',
                          color: '#6366F1',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </span>
                      <div style={{ flex: 1 }}>
                        <MaterialItemSelect
                          value={record.itemId}
                          onChange={(val) => handleItemSelect(record.key, val)}
                          excludeItemIds={items.map((i) => i.itemId)}
                          catalogItems={catalogItems}
                        />
                      </div>
                    </div>
                  </td>

                  {/* Col 2 (colspan 1): Jumlah Diminta */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <Space.Compact style={{ width: '100%' }}>
                      <InputNumber
                        min={1}
                        step={1}
                        value={record.requestedQty}
                        onChange={(newVal) => handleQtyChange(record.key, newVal)}
                        style={{ flex: 1, borderRadius: '8px 0 0 8px', fontWeight: 700 }}
                      />
                      <span
                        style={{
                          background: '#F1F5F9',
                          border: '1px solid #D9D9D9',
                          borderLeft: 0,
                          padding: '0 8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: 12,
                          color: '#475569',
                          borderRadius: '0 8px 8px 0',
                          fontWeight: 600,
                        }}
                      >
                        {record.uomCode}
                      </span>
                    </Space.Compact>
                  </td>

                  {/* Col 3 (colspan 1): Satuan */}
                  <td style={{ padding: '10px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <Tag style={{ fontWeight: 700, fontSize: 12, borderRadius: 4, padding: '4px 8px', margin: 0 }}>
                      {record.uomCode}
                    </Tag>
                  </td>

                  {/* Col 4, 5, 6 (colspan 3): Catatan TextArea */}
                  <td colSpan={3} style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <Input.TextArea
                      rows={2}
                      placeholder="Tuliskan spesifikasi detail, warna, ukuran, atau instruksi khusus..."
                      value={record.notes}
                      onChange={(e) => handleItemNotesChange(record.key, e.target.value)}
                      style={{ borderRadius: 6, fontSize: 12 }}
                    />
                  </td>

                  {/* Aksi Hapus */}
                  <td style={{ padding: '10px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveItem(record.key)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Real-time Summary Card (No Prices/Nominals) */}
        <div
          style={{
            marginTop: 16,
            padding: '14px 20px',
            background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
            borderRadius: 12,
            border: '1px solid #E0E7FF',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: '#6366F1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ringkasan Permintaan
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
              Total Item: <Text strong>{totalItemsCount}</Text> varian material
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>Total Kuantitas Diminta</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#4338CA' }}>
              {totalRequestedUnits.toLocaleString('id-ID')} unit
            </div>
          </div>
        </div>

        <Divider style={{ margin: '20px 0 16px' }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button onClick={onClose} disabled={submitting} style={{ borderRadius: 8 }}>
            Batal
          </Button>

          <Button
            icon={<SaveOutlined />}
            loading={submitting}
            onClick={() => handleSubmit(false)}
            style={{
              background: '#F1F5F9',
              color: '#334155',
              border: '1px solid #CBD5E1',
              borderRadius: 8,
              fontWeight: 600,
            }}
          >
            Simpan sebagai Draft
          </Button>

          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={submitting}
            onClick={() => handleSubmit(true)}
            style={{
              background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              border: 'none',
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            }}
          >
            Simpan & Ajukan Approval ↗
          </Button>
        </div>
      </Form>

      {/* Modal Pemilihan Item Pending dari PR Lain */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined style={{ color: '#D97706', fontSize: 18 }} />
            <span style={{ fontWeight: 800, color: '#0F172A' }}>
              Pilih Item Pending / Belum Disetujui dari PR Sebelumnya
            </span>
          </div>
        }
        open={pendingModalOpen}
        onCancel={() => setPendingModalOpen(false)}
        width={920}
        destroyOnHidden
        footer={[
          <Button key="cancel" onClick={() => setPendingModalOpen(false)} style={{ borderRadius: 8 }}>
            Batal
          </Button>,
          <Button
            key="insert"
            type="primary"
            disabled={selectedPendingKeys.length === 0}
            onClick={handleInsertPendingItems}
            style={{
              background: '#D97706',
              borderColor: '#D97706',
              fontWeight: 700,
              borderRadius: 8,
            }}
          >
            Tambahkan {selectedPendingKeys.length} Item Terpilih ke PR ↗
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 14 }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
            placeholder="Cari berdasarkan nomor PR, nama item, atau kode material..."
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            style={{ borderRadius: 8 }}
            allowClear
          />
        </div>

        <Table
          dataSource={pendingCandidates.filter((c) => {
            if (!pendingSearch.trim()) return true;
            const q = pendingSearch.toLowerCase();
            return (
              c.prNumber.toLowerCase().includes(q) ||
              c.itemName.toLowerCase().includes(q) ||
              c.itemCode.toLowerCase().includes(q) ||
              c.department.toLowerCase().includes(q)
            );
          })}
          columns={[
            {
              title: 'Item Material',
              key: 'item',
              render: (_: unknown, r: PendingPRItemCandidate) => (
                <div>
                  <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>{r.itemName}</div>
                  <div style={{ fontSize: 11, color: '#64748B' }}>{r.itemCode} • {r.category}</div>
                  {r.notes && (
                    <div style={{ fontSize: 11, color: '#475569', fontStyle: 'italic', marginTop: 2 }}>
                      Catatan: &ldquo;{r.notes}&rdquo;
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: 'Asal PR',
              dataIndex: 'prNumber',
              key: 'prNumber',
              width: 170,
              render: (prNo: string, r: PendingPRItemCandidate) => (
                <div>
                  <Tag color="purple" style={{ fontWeight: 700, borderRadius: 4, margin: 0 }}>
                    {prNo}
                  </Tag>
                  <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{r.department}</div>
                </div>
              ),
            },
            {
              title: 'Kuantitas Diminta',
              key: 'qty',
              width: 150,
              align: 'right' as const,
              render: (_: unknown, r: PendingPRItemCandidate) => (
                <span style={{ fontWeight: 700, color: '#1E293B' }}>
                  {r.requestedQty.toLocaleString('id-ID')} {r.uomCode}
                </span>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 110,
              align: 'center' as const,
              render: (status: string) => (
                <Tag color={status === 'REJECTED' ? 'error' : 'blue'} style={{ fontWeight: 600, borderRadius: 4 }}>
                  {status === 'REJECTED' ? 'Ditolak' : 'Pending'}
                </Tag>
              ),
            },
          ]}
          rowKey="prItemId"
          loading={loadingPending}
          rowSelection={{
            selectedRowKeys: selectedPendingKeys,
            onChange: (keys) => setSelectedPendingKeys(keys as string[]),
          }}
          pagination={{ pageSize: 5, size: 'small' }}
          size="small"
        />
      </Modal>
    </Modal>
  );
}
